import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

/** Search on the server: the catalog endpoints return at most 200 rows per query. */
export function RemotePicker<T extends { id: string }>({
  id,
  value,
  selectedLabel,
  label,
  placeholder,
  clearLabel,
  emptyLabel,
  errorLabel,
  refineLabel,
  search,
  itemLabel,
  onSelect,
  compact = false,
}: {
  id?: string;
  value: string | null;
  selectedLabel: string | null;
  label: string;
  placeholder: string;
  clearLabel: string;
  emptyLabel: string;
  errorLabel: string;
  refineLabel: string;
  search: (query: string) => Promise<T[]>;
  itemLabel: (item: T) => string;
  onSelect: (item: T | null) => void;
  compact?: boolean;
}) {
  const listId = useId();
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const searchRef = useRef(search);
  searchRef.current = search;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);

  const options: (T | null)[] = value ? [null, ...results.slice(0, 30)] : results.slice(0, 30);

  useEffect(() => {
    if (!open) return;
    let current = true;
    setLoading(true);
    setFailed(false);
    setResults([]);
    const timer = window.setTimeout(() => {
      void searchRef.current(query ?? '').then((items) => {
        if (current) setResults(items);
      }).catch(() => {
        if (current) setFailed(true);
      }).finally(() => {
        if (current) setLoading(false);
      });
    }, query ? 200 : 0);
    return () => { current = false; window.clearTimeout(timer); };
  }, [open, query]);

  useLayoutEffect(() => {
    if (!open || !input.current) return;
    const rect = input.current.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - 12;
    const maxHeight = Math.min(320, Math.max(120, below));
    setPlace({
      top: below < 160 && rect.top > 160 ? Math.max(8, rect.top - maxHeight - 4) : rect.bottom + 4,
      left: Math.min(rect.left, Math.max(8, window.innerWidth - Math.max(rect.width, compact ? 280 : 0) - 8)),
      width: Math.max(rect.width, compact ? 280 : 0),
      maxHeight,
    });
  }, [open, compact]);

  useEffect(() => {
    if (!open) return;
    const away = (event: Event) => {
      if (event.target instanceof Node && root.current?.contains(event.target)) return;
      close();
    };
    const scrollAway = (event: Event) => {
      if (event.target instanceof Node && root.current?.contains(event.target)) return;
      close();
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('focusin', away);
    window.addEventListener('scroll', scrollAway, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('focusin', away);
      window.removeEventListener('scroll', scrollAway, true);
      window.removeEventListener('resize', close);
    };
  });

  function close() {
    setOpen(false);
    setQuery(null);
  }

  function pick(item: T | null) {
    onSelect(item);
    close();
  }

  return (
    <div className="combo invoice-remote-picker" ref={root}>
      <input
        id={id}
        ref={input}
        className={compact ? 'inp sm' : 'inp'}
        role="combobox"
        aria-label={id ? undefined : label}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && options[active]
          ? `${listId}-${options[active]?.id}` : undefined}
        autoComplete="off"
        placeholder={placeholder}
        value={query ?? selectedLabel ?? ''}
        onFocus={() => { setQuery(''); setActive(value ? 1 : 0); setOpen(true); }}
        onClick={() => { if (!open) { setQuery(''); setActive(value ? 1 : 0); setOpen(true); } }}
        onChange={(event) => { setQuery(event.target.value); setActive(value ? 1 : 0); setOpen(true); }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((index) => Math.max(0, Math.min(index + 1, options.length - 1)));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((index) => Math.max(index - 1, 0));
          } else if (event.key === 'Enter' && open) {
            event.preventDefault();
            const chosen = options[active];
            if (!loading && !failed && chosen !== undefined) pick(chosen);
          } else if (event.key === 'Escape') {
            event.preventDefault();
            close();
          }
        }}
      />
      {open && place && (
        <ul id={listId} role="listbox" className="combo-list"
          style={{ top: place.top, left: place.left, width: place.width, maxHeight: place.maxHeight }}>
          {!loading && !failed && options.length === 0 && (
            <li className="combo-empty" aria-disabled="true">{emptyLabel}</li>
          )}
          {loading && <li className="combo-empty" aria-disabled="true">…</li>}
          {failed && <li className="combo-empty" aria-disabled="true">{errorLabel}</li>}
          {!loading && !failed && options.map((item, index) => (
            <li key={item?.id ?? 'clear'} id={`${listId}-${item?.id ?? 'clear'}`}
              role="option" aria-selected={index === active}
              className={index === active ? 'combo-item active' : 'combo-item'}
              onMouseDown={(event) => { event.preventDefault(); pick(item); }}
              onMouseEnter={() => setActive(index)}>
              <span className="combo-name">{item ? itemLabel(item) : clearLabel}</span>
            </li>
          ))}
          {!loading && !failed && results.length > 30 && (
            <li className="combo-empty" aria-disabled="true">{refineLabel}</li>
          )}
        </ul>
      )}
    </div>
  );
}
